import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { config } from "./config";

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge to handle conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {}
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate" ? accurateSizes[i] ?? "Bytest" : sizes[i] ?? "Bytes"
  }`;
}

/**
 * Truncate text to a certain length
 */
export function truncate(str: string, length: number) {
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

/**
 * Format date to relative time string
 * Safely handles Date objects, strings, and numbers
 * Uses UTC timezone to avoid timezone shifts when formatting dates
 */
export function formatDate(date: Date | string | number) {
  // If it's already a Date object, use it directly
  // Otherwise convert it to a Date
  const dateObj = date instanceof Date ? date : new Date(date);

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(dateObj);
}

/**
 * Safely convert a value to a Date object
 * Returns null if the value is null/undefined
 */
export function toDate(value: Date | string | number | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  return new Date(value);
}

/**
 * Absolute URL helper
 */
export function absoluteUrl(path: string) {
  const baseUrl = config.app.url.replace(/\/$/, ''); // Remove trailing slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Format currency to USD
 */
export function formatCurrency(amount: number) {
  return new Intl.NumberFormat(config.payment.locale, {
    style: "currency",
    currency: config.payment.currency,
  }).format(amount);
}

/**
 * Calculate quote line item total
 */
export function calculateLineItemTotal(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

/**
 * Calculate quote subtotal from line items
 */
export function calculateQuoteSubtotal(lineItems: Array<{ quantity: number; unitPrice: number }>): number {
  return lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item.quantity, item.unitPrice), 0);
}

/**
 * Calculate tax amount
 */
export function calculateTax(subtotal: number, taxRate: number = config.tax.defaultTaxRate): number {
  return subtotal * taxRate;
}

/**
 * Calculate quote total
 */
export function calculateQuoteTotal(subtotal: number, tax: number): number {
  return subtotal + tax;
}

/**
 * Get quote status color
 * @deprecated Use getQuoteStatusColor from '@/lib/theme-colors' instead
 */
export { getQuoteStatusColor } from "./theme-colors";

/**
 * Get invoice status color
 * @deprecated Use getInvoiceStatusColor from '@/lib/theme-colors' instead
 */
export { getInvoiceStatusColor } from "./theme-colors";

/**
 * Format time from 24-hour format (HH:MM) to 12-hour format (h:MM AM/PM)
 * @param time24 - Time in 24-hour format (e.g., "17:00", "09:30")
 * @returns Time in 12-hour format (e.g., "5:00 PM", "9:30 AM") or original value if invalid
 */
export function formatTime12Hour(time24: string | null | undefined): string {
  if (!time24) return "";

  try {
    // Parse the time string (format: "HH:MM")
    const [hours24Str, minutes] = time24.split(":");
    const hours24 = parseInt(hours24Str, 10);

    if (isNaN(hours24) || hours24 < 0 || hours24 > 23) {
      return time24; // Return original if invalid
    }

    // Convert to 12-hour format
    const period = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;

    return `${hours12}:${minutes} ${period}`;
  } catch (error) {
    return time24; // Return original if parsing fails
  }
}
